<template>
  <div class="panel-container-2024-1226-1400">
    <!-- 顶部搜索栏 -->
    <div class="top-container">
      <el-form class="power-form" inline :model="queryForm">
        <el-form-item :label="$str.getKey('数据分析/日期')">
          <el-date-picker v-model="queryForm.timer" @change="timeRangeChange" :disabled-date="disabledDate"
            :range-separator="$str.getKey('数据分析/至')" :clearable="false" type="daterange" format="YYYY-MM-DD"
            size="default" class="filter-date-picker"></el-date-picker>
        </el-form-item>
      </el-form>
      <el-form-item>
        <div class="filter-actions">
          <v-btn color="#2655f1" rounded="xl" class="action-btn" @click="onSearch">
            <v-icon start icon="mdi-magnify" />{{ $str.getKey('数据分析/查询') }}
          </v-btn>
          <v-btn variant="outlined" color="#2655f1" rounded="xl" class="action-btn" @click="onReset">
            <v-icon start icon="mdi-refresh" />{{ $str.getKey('数据分析/重置') }}
          </v-btn>
        </div>
      </el-form-item>
    </div>
    <div class="main-container">
      <el-scrollbar height="100%">
        <div class="scroll-content-container">
          <!-- 关口 -->
          <div class="charts-panel gate-way">
            <div class="panel-title">{{ $str.getKey('数据分析/关口表') }}</div>
            <div class="charts-container">
              <div v-for="(row, rowIdx) in chartListData.gateway" :key="rowIdx"
                :class="['row-container', `row${rowIdx}`]">
                <div v-for="(item, colIdx) in row" :key="colIdx"
                  :class="['card-container', `row${rowIdx}-col${colIdx}`]">
                  <ChartComp :title="item.name" :chartData="item.chartData" :timeRange="queryForm.timer"></ChartComp>
                </div>
              </div>
              <div v-if="chartListData.gateway.length === 0 && !loading" class="row-container">
                <el-empty :image="noDataIcon" class="empty card-container"></el-empty>
              </div>
              <!-- 加载状态 -->
              <div v-if="loading" class="chart-status">
                <div class="status-content">
                  <span class="loading-text">{{ $str.getKey('数据分析/加载中...') }}</span>
                </div>
              </div>
            </div>
          </div>
          <!-- 并网点 -->
          <div class="charts-panel gate-way">
            <div class="panel-title">{{ $str.getKey('数据分析/并网表') }}</div>
            <div class="charts-container">
              <div v-for="(row, rowIdx) in chartListData.gridConnection" :key="row"
                :class="['row-container', `row${rowIdx}`]">
                <div v-for="(item, colIdx) in row" :key="item" :class="['card-container', `row${rowIdx}-col${colIdx}`]">
                  <ChartComp :title="item.name" :chartData="item.chartData" :timeRange="queryForm.timer"></ChartComp>
                </div>
              </div>
              <div v-if="chartListData.gridConnection.length === 0 && !loading" class="row-container">
                <el-empty :image="noDataIcon" class="empty card-container"></el-empty>
              </div>
              <!-- 加载状态 -->
              <div v-if="loading" class="chart-status">
                <div class="status-content">
                  <span class="loading-text">{{ $str.getKey('数据分析/加载中...') }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </el-scrollbar>
    </div>

  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { queryMetersThreePhaseVoltage } from "../api/index.js";
import { cloneDeep } from "@/utils"
import dayjs from "dayjs";
import ChartComp from './chartComp/index.vue';
import { useAppStore } from "@/store/modules/app";
import noDataIcon from "@/assets/production/noData.png";

const $str = window.$str;
const appStore = useAppStore();
/** 获取stationId，并监听stationId的变化，触发stationIdWatch进行响应 */
const { stationId } = appStore.useStationState({
  stationIdWatch() {
    initPageData();
  }
})

const origin = {
  queryForm: {
    timer: [dayjs(), dayjs()],
  },
  chartListData: {
    gateway: [], // 关口    
    gridConnection: [], // 并网
  },
  meterData: {
    name: '',
    chartData: [],
    meterType: NaN,
    meterId: '',
  }
}

const disabledDate = (date) => {
  return date > Date.now() || date < dayjs().subtract(6, 'month');
}
const queryForm = reactive(cloneDeep(origin.queryForm));
const loading = ref(false);
const chartListData = reactive(cloneDeep(origin.chartListData));


onMounted(() => { })


/** 时间范围发生变化 */
async function initPageData() {
  onSearch();
}


/** 时间范围发生变化 */
function timeRangeChange() {
  onSearch();
}


/** 查询 */
function onSearch() {
  getChartsData();
}

/** 重置 */
function onReset() {
  queryForm.timer = origin.queryForm.timer;
  getChartsData();
}


/** 获取所有卡片图表的数据 */
function getChartsData() {
  const params = {
    startTime: dayjs(queryForm.timer[0]).format('YYYY-MM-DD 00:00:00'),
    endTime: dayjs(queryForm.timer[1]).format('YYYY-MM-DD 23:59:59'),
    stationId: stationId.value,
  }
  loading.value = true;
  queryMetersThreePhaseVoltage(params).then(res => {
    if (Array.isArray(res?.data?.gateway)) {
      chartListData.gateway = formatRowsData(res.data.gateway);
    } else {
      chartListData.gateway = [];
    }

    if (Array.isArray(res?.data?.gridConnection)) {
      chartListData.gridConnection = formatRowsData(res.data.gridConnection);
    } else {
      chartListData.gridConnection = [];
    }
  }).catch(err => {
    console.error(err)
    chartListData.gateway = [];
    chartListData.gridConnection = [];
  }).finally(() => {
    loading.value = false;
  })
}

/** 将数据列表格式化成多行图表数据，每行最多展示3个图表 */
function formatRowsData(dataList = []) {
  const len = dataList.length;
  const rowsData = [];
  for (let idx = 0; idx < len; idx += 3) {
    const rowItem = [];
    const chart1Data = formatChartData(dataList[idx]);
    rowItem.push(chart1Data);
    if (idx + 1 < len) {
      const chart2Data = formatChartData(dataList[idx + 1]);
      rowItem.push(chart2Data);
    }
    if (idx + 2 < len) {
      const chart3Data = formatChartData(dataList[idx + 2]);
      rowItem.push(chart3Data);
    }
    rowsData.push(rowItem);
  }
  return rowsData;
}


/** 格式化图表数据 */
function formatChartData(data) {
  const meterData = cloneDeep(origin.meterData);
  meterData.name = data.name ?? '';
  meterData.chartData = data.points ?? [];
  meterData.meterType = data.meterType ?? NaN;
  meterData.meterId = data.meterId ?? '';
  return meterData;
}

</script>
<style lang="scss" scoped>
.panel-container-2024-1226-1400 {
  font-family: 'HarmonyOS Sans SC', sans-serif;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 0;

  .top-container {
    padding: 14px 20px;
    box-sizing: border-box;
    background: rgba(255, 255, 255, 1);
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 12px;

    .power-form {
      flex: 1 1 auto;
      min-width: 0;
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 16px;
    }

    .el-form-item {
      margin-bottom: 0;
      margin-right: 0;
      display: flex;
      align-items: center;
      height: 40px;

      :deep(.el-form-item__label) {
        font-size: 14px;
        color: rgba(46, 46, 46, 0.85);
        font-weight: 400;
        padding-right: 12px;
        line-height: 40px;
        height: 40px;
        display: flex;
        align-items: center;
      }

      :deep(.el-form-item__content) {
        line-height: 40px;
        display: flex;
        align-items: center;

        .el-range-editor {
          width: 260px !important;
          min-width: 260px !important;
          max-width: 260px !important;
        }
      }
    }

    .filter-date-picker {

      :deep(.el-input__wrapper) {
        width: 100% !important;
        max-width: 100% !important;
        background: #fff;
        border: 1px solid rgba(192, 207, 218, 0.3);
        border-radius: 20px;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
        padding: 0 16px;
        min-height: 40px;
        height: 40px;
        transition: border-color 0.2s;

        &:hover {
          border-color: #c0cfda;
        }

        &.is-focus {
          border-color: #3a68ff;
        }
      }

      :deep(.el-input__inner) {
        font-family: "HarmonyOS Sans SC", sans-serif;
        font-size: 14px;
        color: #2E2E2E;
        height: 40px;
        line-height: 40px;

        &::placeholder {
          color: rgba(46, 46, 46, 0.5);
          font-weight: 400;
          font-family: "HarmonyOS Sans SC", sans-serif;
        }
      }

      :deep(.el-input__suffix) {
        color: rgba(46, 46, 46, 0.5);
      }

      :deep(.el-range-input) {
        font-family: "HarmonyOS Sans SC", sans-serif;
        font-size: 14px;
        color: #2E2E2E;
      }

      :deep(.el-range-separator) {
        font-family: "HarmonyOS Sans SC", sans-serif;
        font-size: 14px;
        color: #2E2E2E;
      }
    }

    .filter-actions {
      display: flex;
      align-items: center;
      gap: 12px;
      flex-shrink: 0;
      height: 40px;
    }
  }

  .main-container {
    flex: 1;
    width: 100%;
    background: rgba(255, 255, 255, 1);
    border-radius: 0 0 20px 20px;
    overflow: hidden;

    .scroll-content-container {
      width: 100%;
      height: auto;
      padding: 0 20px;
      display: flex;
      flex-direction: column;
      gap: 20px;

      .charts-panel {
        width: 100%;
        height: auto;
        display: flex;
        flex-direction: column;
        gap: 10px;

        .panel-title {
          font-family: 'HarmonyOS Sans SC', sans-serif;
          font-size: 16px;
          color: rgba(46, 46, 46, 1);
          font-weight: 500;
        }

        .charts-container {
          width: 100%;
          height: auto;
          display: flex;
          flex-direction: column;
          gap: 10px;
          position: relative;
          min-height: 470px;

          .row-container {
            width: 100%;
            height: 470px;
            display: flex;
            gap: 10px;

            .card-container {
              flex: 1;
              width: 0;
              height: 100%;
              min-width: 0;
              background: transparent;
              border: none;
              overflow: hidden;
            }
          }

          .chart-status {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(255, 255, 255, 0.9);
            z-index: 10;

            .status-content {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              gap: 12px;

              .loading-text,
              .empty-text {
                font-family: "HarmonyOS Sans SC", sans-serif;
                font-size: 14px;
                color: rgba(46, 46, 46, 0.65);
                font-weight: 400;
              }
            }
          }
        }
      }
    }
  }
}
</style>
